import React, { useMemo, useState } from "react";
import { X } from "lucide-react";
import MAP from "./argentinaMap.json";

const SCALE = ["#FEF3C7", "#FCD34D", "#F5B400", "#D97706", "#0A2463"];
const EMPTY = "#E5E7EB";

const colorFor = (n, max) => {
  if (!n) return EMPTY;
  const i = Math.min(SCALE.length - 1, Math.floor((n / max) * SCALE.length - 1e-9));
  return SCALE[Math.max(0, i)];
};

// Choropleth of envíos per province; click a province to see approved ones.
export const ArgentinaMap = ({ data = [] }) => {
  const [sel, setSel] = useState(null);
  const [hover, setHover] = useState(null);
  const byProv = useMemo(() => Object.fromEntries(data.map((d) => [d.provincia, d])), [data]);
  const max = Math.max(1, ...data.map((d) => d.envios));
  const total = data.reduce((a, d) => a + d.envios, 0);
  const info = (n) => byProv[n] || { provincia: n, envios: 0, aprobados: 0 };
  const caba = MAP.provincias.find((p) => p.nombre === "CABA");
  const shown = sel ? info(sel) : null;

  const handlers = (n) => ({
    onClick: () => { setSel(n); setHover(null); },
    onPointerEnter: (e) => e.pointerType === "mouse" && setHover(n),
    onPointerLeave: () => setHover(null),
    role: "button",
    tabIndex: 0,
    onKeyDown: (e) => (e.key === "Enter" || e.key === " ") && setSel(n),
    "aria-label": `${n}: ${info(n).envios} envíos`,
    "data-testid": `mapa-prov-${n}`,
  });

  return (
    <div className="bg-white border border-gray-200 p-5" data-testid="stats-mapa">
      <div className="flex flex-wrap justify-between items-start gap-3 mb-3">
        <div>
          <h3 className="font-headings uppercase tracking-wide text-[#0A192F]">Mapa de aportes</h3>
          <p className="text-xs text-gray-500">Envíos recibidos por provincia · {total} en total. Tocá una provincia para ver los aprobados.</p>
        </div>
        <div className="flex items-center gap-1 text-[10px] text-gray-500" data-testid="mapa-leyenda">
          <span className="w-4 h-3 inline-block border" style={{ background: EMPTY }} /> 0
          {SCALE.map((c) => <span key={c} className="w-4 h-3 inline-block" style={{ background: c }} />)}
          <span>{max}</span>
        </div>
      </div>
      <div className="grid md:grid-cols-[minmax(0,320px)_1fr] gap-6 items-start">
        <div className="relative mx-auto w-full max-w-[320px]">
          <svg viewBox={`-4 -4 ${MAP.w + 8} ${MAP.h + 8}`} className="w-full h-auto" data-testid="mapa-svg">
            {MAP.provincias.filter((p) => p.nombre !== "CABA").sort((a, b) => (a.nombre === sel) - (b.nombre === sel)).map((p) => (
              <path
                key={p.nombre}
                d={p.d}
                fill={colorFor(info(p.nombre).envios, max)}
                stroke={sel === p.nombre ? "#0A192F" : "#FFFFFF"}
                strokeWidth={sel === p.nombre ? 2 : 0.8}
                className="cursor-pointer outline-none transition-[opacity] duration-150 hover:opacity-80 focus:opacity-80"
                {...handlers(p.nombre)}
              />
            ))}
            {caba && (
              <circle cx={caba.cx} cy={caba.cy} r={5} fill={colorFor(info("CABA").envios, max)} stroke="#0A192F" strokeWidth={sel === "CABA" ? 2 : 1} className="cursor-pointer" {...handlers("CABA")} />
            )}
          </svg>
          {hover && (
            <div className="absolute top-2 right-0 bg-[#0A192F] text-white text-xs px-2.5 py-1.5 pointer-events-none" data-testid="mapa-tooltip">
              {hover}: <strong>{info(hover).envios}</strong> envíos
            </div>
          )}
        </div>
        <div>
          {shown ? (
            <div className="border-l-4 border-[#F5B400] bg-[#0A192F] text-white p-5" data-testid="mapa-detalle">
              <div className="flex justify-between items-start">
                <div className="font-headings uppercase tracking-wide text-[#F5B400]" data-testid="mapa-detalle-nombre">{shown.provincia}</div>
                <button onClick={() => setSel(null)} className="text-white/60 hover:text-white" data-testid="mapa-detalle-cerrar"><X size={16} /></button>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <div><div className="text-[10px] uppercase tracking-wider text-white/60">Envíos</div><div className="font-headings text-3xl font-bold" data-testid="mapa-detalle-envios">{shown.envios}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-white/60">Aprobados</div><div className="font-headings text-3xl font-bold text-[#F5B400]" data-testid="mapa-detalle-aprobados">{shown.aprobados}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-white/60">Tasa</div><div className="font-headings text-3xl font-bold">{shown.envios ? Math.round((shown.aprobados / shown.envios) * 100) : 0}%</div></div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Elegí una provincia en el mapa.</p>
          )}
          <ol className="mt-4 space-y-1.5 text-sm" data-testid="mapa-ranking">
            {data.slice(0, 8).map((d, i) => (
              <li key={d.provincia}>
                <button onClick={() => setSel(d.provincia)} className="w-full flex items-center gap-2 text-left hover:bg-gray-50 px-1">
                  <span className="w-5 text-xs text-gray-400 tabular-nums">{i + 1}</span>
                  <span className="flex-1">{d.provincia}</span>
                  <span className="h-2 bg-[#F5B400]" style={{ width: `${(d.envios / max) * 80}px` }} />
                  <span className="w-8 text-right tabular-nums font-semibold">{d.envios}</span>
                </button>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
};

export default ArgentinaMap;
